import { Card, Colors, Heading, Text, Toggle } from '@amelio/ui-library';

import {
    CommunicationEmailAudience,
    CommunicationEmailCard,
    type CommunicationEmailCardProps,
} from '../CommunicationEmailCard/CommunicationEmailCard';

import { useAmelioTranslations } from '@/hooks';

export interface CommunicationBlockProps {
    title: string;
    blockEnabled: boolean;
    onBlockEnabledChange: (enabled: boolean) => void;
    managerCardProps: Omit<CommunicationEmailCardProps, 'audience'>;
    /** `undefined` when this milestone carries no employee card at all (corrected matrix). */
    employeeCardProps?: Omit<CommunicationEmailCardProps, 'audience'>;
    zIndex: number;
    isReadOnly?: boolean;
}

export const CommunicationBlock = ({
    title,
    blockEnabled,
    onBlockEnabledChange,
    managerCardProps,
    employeeCardProps,
    zIndex,
    isReadOnly = false,
}: CommunicationBlockProps) => {
    const { t } = useAmelioTranslations();

    const handleBlockEnabledChange = (next: boolean) => {
        if (isReadOnly) {
            return;
        }

        onBlockEnabledChange(next);
    };

    return (
        <Card
            bgColor={'bg-white/60'}
            borderRadius={'xl'}
            padding={'md'}
            variant={'flat'}
            className={'flex flex-col gap-4'}
            zIndex={zIndex}
            dataTestId={'communication-block'}
        >
            <div className={'flex items-center gap-4'}>
                <Toggle
                    checked={blockEnabled}
                    onChange={handleBlockEnabledChange}
                    readOnly={isReadOnly}
                    size={'smPlus'}
                    ariaLabel={t('cycleEvaluationCommunications.block.enableAriaLabel', { title })}
                    dataTestId={'communication-block-toggle'}
                />

                <Heading
                    level={5}
                    weight={'bold'}
                >
                    {title}
                </Heading>
            </div>

            {blockEnabled ? (
                <div className={'flex flex-col gap-4'}>
                    <CommunicationEmailCard
                        audience={CommunicationEmailAudience.MANAGER}
                        {...managerCardProps}
                    />

                    {employeeCardProps ? (
                        <CommunicationEmailCard
                            audience={CommunicationEmailAudience.EMPLOYEE}
                            {...employeeCardProps}
                        />
                    ) : null}
                </div>
            ) : (
                <Text
                    size={'small'}
                    color={Colors.MUTED}
                >
                    {t('cycleEvaluationCommunications.block.disabledNotice')}
                </Text>
            )}
        </Card>
    );
};
